import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import React, { useState } from 'react';
import { TranslationFunction } from '../../components/blocks';
import { TruncatedText } from '../../components/composites';
import { ResellerItem, SalesOrderDemandItem, SalesOrderDemandShipment } from '../../redux/types';
import { findResellerItem } from '../../utils/resellerItemUtils';
import { toFamilyTranslationKey } from '../../utils/textUtils';
import { ShipmentDetails } from './ShipmentDetails';

export const AsinTable = ({
    items,
    expandedAsins,
    toggleAsin,
    resellerItems,
    shipments,
    expandAllAsins,
    collapseAllAsins,
    requestedShipDate,
    requestedDeliveryDate,
    totals,
    t,
    timezone,
}: {
    items: SalesOrderDemandItem[];
    expandedAsins: string[];
    toggleAsin: (asin: string) => void;
    resellerItems: ResellerItem[];
    shipments: SalesOrderDemandShipment[];
    expandAllAsins: () => void;
    collapseAllAsins: () => void;
    requestedShipDate?: number;
    requestedDeliveryDate?: number;
    totals: {
        ordered: number;
        open: number;
        released: number;
        shipped: number;
        canceled: number;
    };
    t: TranslationFunction;
    timezone?: string;
}) => {
    const [sortColumn, setSortColumn] = useState<string>();
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>();

    const handleSort = ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => {
        setSortColumn(sortColumn);
        setSortDirection(sortDirection as 'ascending' | 'descending');
    };

    const hasShipmentsForAsin = (asin?: string) => shipments.some((s) => s.items?.some((si) => si.asin === asin));

    const sortedItems = [...items].sort((a, b) => {
        const aHasShipments = hasShipmentsForAsin(a.asin) ? 0 : 1;
        const bHasShipments = hasShipmentsForAsin(b.asin) ? 0 : 1;
        if (aHasShipments !== bHasShipments) return aHasShipments - bHasShipments;

        if (!sortColumn) return 0;
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
            case 'asin':
                aValue = a.asin;
                bValue = b.asin;
                break;
            case 'resellerSku':
                aValue = findResellerItem(resellerItems, a.asin)?.externalSku || '';
                bValue = findResellerItem(resellerItems, b.asin)?.externalSku || '';
                break;
            case 'description':
                aValue = findResellerItem(resellerItems, a.asin)?.title || '';
                bValue = findResellerItem(resellerItems, b.asin)?.title || '';
                break;
            case 'family':
                aValue = findResellerItem(resellerItems, a.asin)?.family || '';
                bValue = findResellerItem(resellerItems, b.asin)?.family || '';
                break;
            case 'orderedQuantity':
                aValue = a.orderedQuantity;
                bValue = b.orderedQuantity;
                break;
            case 'releasedQuantity':
                aValue = a.releasedQuantity;
                bValue = b.releasedQuantity;
                break;
            case 'unitsShipped':
                aValue = a.shippedQuantity;
                bValue = b.shippedQuantity;
                break;
            case 'openQuantity':
                aValue = a.openQuantity;
                bValue = b.openQuantity;
                break;
            case 'canceledQuantity':
                aValue = a.canceledQuantity;
                bValue = b.canceledQuantity;
                break;
            default:
                return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortDirection === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'ascending' ? aValue - bValue : bValue - aValue;
        }
        return 0;
    });

    const expandableAsins = items
        .filter((item) => shipments.some((s) => s.items?.some((si) => si.asin === item.asin)))
        .map((item) => item.asin!);

    const toggleExpandAll = () => {
        const allExpanded = expandableAsins.every((asin) => expandedAsins.includes(asin));
        if (allExpanded) {
            collapseAllAsins();
        } else {
            expandAllAsins();
        }
    };

    const renderTableRows = () => {
        const rows: React.ReactElement[] = [];
        sortedItems.forEach((item) => {
            const resellerItem = findResellerItem(resellerItems, item.asin);
            const hasShipments = hasShipmentsForAsin(item.asin);

            rows.push(
                <TableRow key={item.asin} highlightOnHover={true}>
                    <TableCell>
                        {hasShipments && (
                            <Button type={'icon'} size={'small'} onClick={() => toggleAsin(item.asin)}>
                                <Icon
                                    tokens={
                                        expandedAsins.includes(item.asin)
                                            ? chevronDownSmallTokens
                                            : chevronRightSmallTokens
                                    }
                                >
                                    {expandedAsins.includes(item.asin)
                                        ? t('collapse-asinDetails')
                                        : t('expand-asinDetails')}
                                </Icon>
                            </Button>
                        )}
                    </TableCell>
                    <TableCell>{item.asin}</TableCell>
                    <TableCell>{resellerItem?.externalSku || ''}</TableCell>
                    <TableCell>
                        {resellerItem?.family
                            ? t(toFamilyTranslationKey(resellerItem.family), {
                                  defaultValue: resellerItem.family,
                              })
                            : ''}
                    </TableCell>
                    <TableCell>
                        <TruncatedText noWrap={true} text={t.cognate(resellerItem?.title || '')} maxWidth={40} />
                    </TableCell>
                    <TableCell>{item.orderedQuantity}</TableCell>
                    <TableCell>{item.openQuantity}</TableCell>
                    <TableCell>{item.releasedQuantity}</TableCell>
                    <TableCell>{item.shippedQuantity}</TableCell>
                    <TableCell>{item.canceledQuantity}</TableCell>
                </TableRow>
            );

            if (expandedAsins.includes(item.asin!) && hasShipments) {
                const itemShipments = shipments.filter((s) => s.items?.some((si) => si.asin === item.asin));
                rows.push(
                    <TableRow key={`${item.asin}-shipments-subtable`} backgroundColor={'secondary'}>
                        <TableCell columnSpan={10}>
                            <ShipmentDetails
                                shipments={itemShipments}
                                asin={item.asin}
                                requestedShipDate={requestedShipDate}
                                requestedDeliveryDate={requestedDeliveryDate}
                                t={t}
                                timezone={timezone}
                            />
                        </TableCell>
                    </TableRow>
                );
            }
        });
        return rows;
    };

    return (
        <Table
            headerRows={1}
            showDividers={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            data-testid={'poViewAsinTable'}
        >
            <TableRow>
                <TableCell width={60}>
                    {expandableAsins.length > 0 && (
                        <Button type={'icon'} size={'small'} onClick={toggleExpandAll}>
                            <Icon
                                tokens={
                                    expandedAsins.length === expandableAsins.length
                                        ? chevronDownSmallTokens
                                        : chevronRightSmallTokens
                                }
                            >
                                {t('purchaseOrderListPage:expandCollapseAll-label')}
                            </Icon>
                        </Button>
                    )}
                </TableCell>
                <TableCell sortColumn={'asin'}>{t('purchaseOrderListPage:asin-column')}</TableCell>
                <TableCell sortColumn={'resellerSku'}>{t('purchaseOrderListPage:retailerSku-column')}</TableCell>
                <TableCell sortColumn={'family'}>{t('purchaseOrderListPage:family-column')}</TableCell>
                <TableCell sortColumn={'description'}>{t('purchaseOrderListPage:productDescription-column')}</TableCell>
                <TableCell sortColumn={'orderedQuantity'}>{t('purchaseOrderListPage:unitsOrdered-column')}</TableCell>
                <TableCell sortColumn={'openQuantity'}>{t('purchaseOrderListPage:unitsOpen-column')}</TableCell>
                <TableCell sortColumn={'releasedQuantity'}>{t('purchaseOrderListPage:unitsReleased-column')}</TableCell>
                <TableCell sortColumn={'unitsShipped'}>{t('purchaseOrderListPage:unitsShipped-column')}</TableCell>
                <TableCell sortColumn={'canceledQuantity'}>{t('purchaseOrderListPage:unitsCanceled-column')}</TableCell>
            </TableRow>
            <TableRow key={'totals'}>
                <TableCell />
                <TableCell>
                    <strong>
                        <em>{t('common:total-label')}</em>
                    </strong>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell>
                    <strong>{totals.ordered}</strong>
                </TableCell>
                <TableCell>
                    <strong>{totals.open}</strong>
                </TableCell>
                <TableCell>
                    <strong>{totals.released}</strong>
                </TableCell>
                <TableCell>
                    <strong>{totals.shipped}</strong>
                </TableCell>
                <TableCell>
                    <strong>{totals.canceled}</strong>
                </TableCell>
            </TableRow>
            {renderTableRows()}
        </Table>
    );
};
